/**
 * API Configuration
 * 
 * Centralized API URL management
 * All API endpoints and base URLs are defined here
 */

// Base URLs
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";


export const API_PREFIX = "/api";

// Full Base URL with prefix
export const API_URL = `${API_BASE_URL}${API_PREFIX}`;

// API Endpoints
export const API_ENDPOINTS = {
  EXPORT_JOBS: {
    BASE: `${API_PREFIX}/exports/jobs`,
    BY_ID: (id) => `${API_PREFIX}/exports/jobs/${id}`,
    DOWNLOAD: (id) => `${API_PREFIX}/exports/jobs/${id}/download`,
  },

  // Global AI assistant
  AI: {
    CHAT: `${API_PREFIX}/ai/chat`,
    CHAT_SESSION: (sessionId) => `${API_PREFIX}/ai/chat/sessions/${sessionId}`,
  },

  // Auth endpoints
  AUTH: {
    LOGIN_DOCTOR: `${API_PREFIX}/auth/login-doctor`,
    TENANT_REGISTER: `${API_PREFIX}/auth/tenant/register`,
    TENANT_LOGIN: `${API_PREFIX}/auth/tenant/login`,
    TENANT_REGISTER_DOCTOR: `${API_PREFIX}/auth/tenant/register-doctor`,
    TENANT_INVITE_ADMIN: `${API_PREFIX}/auth/tenant/invite-admin`,
    ADMIN_REGISTER_DOCTOR: `${API_PREFIX}/auth/admin/register-doctor`,
    ADMIN_INVITE_DOCTOR: `${API_PREFIX}/auth/admin/invite-doctor`,
    INDIVIDUAL_DOCTOR_REGISTER: `${API_PREFIX}/auth/register-individual-doctor`,
    COMPLETE_INVITATION: `${API_PREFIX}/auth/complete-invitation`,
    REFRESH_TOKEN: `${API_PREFIX}/auth/refresh-token`,
    ACCEPT_TERMS: `${API_PREFIX}/auth/accept-terms`,
    ACCEPT_LIMITED_DATA_PROCESSING: `${API_PREFIX}/auth/accept-limited-data-processing`,
    // Password Reset endpoints
    PASSWORD_FORGOT: `${API_PREFIX}/auth/password/forgot`,
    PASSWORD_VERIFY_OTP: `${API_PREFIX}/auth/password/verify-otp`,
    PASSWORD_RESET: `${API_PREFIX}/auth/password/reset`,
    // 2FA (opsiyonel e-posta tabanlı 2 Adımlı Doğrulama)
    TWO_FACTOR_STATUS: `${API_PREFIX}/auth/2fa/status`,
    TWO_FACTOR_ENABLE_REQUEST: `${API_PREFIX}/auth/2fa/enable/request`,
    TWO_FACTOR_ENABLE_VERIFY: `${API_PREFIX}/auth/2fa/enable/verify`,
    TWO_FACTOR_DISABLE: `${API_PREFIX}/auth/2fa/disable`,
    TWO_FACTOR_VERIFY_LOGIN: `${API_PREFIX}/auth/2fa/verify-login`,
    VERIFY_PASSWORD: `${API_PREFIX}/auth/verify-password`,
    // Kiosk PIN endpoints (D7)
    KIOSK_PIN_STATUS: `${API_PREFIX}/auth/kiosk-pin/status`,
    KIOSK_PIN: `${API_PREFIX}/auth/kiosk-pin`,
    KIOSK_PIN_RESET: `${API_PREFIX}/auth/kiosk-pin/reset`,
    VERIFY_KIOSK_PIN: `${API_PREFIX}/auth/verify-kiosk-pin`,
  },

  /**
   * Oturum açmış kullanıcı UI tercihleri (doctor / tenant / super-admin).
   * Sözleşme: `appearance` = "light" | "dark" | "system"
   * Sunucu yalnızca tercihi saklar; resolved dark/light cihaz tarafında çözülür.
   */
  ME: {
    PREFERENCES: `${API_PREFIX}/me/preferences`,
  },

  // Patient endpoints
  PATIENTS: {
    SUGGESTIONS: `${API_PREFIX}/patients/suggestions`,
    ADD: `${API_PREFIX}/patients/add`,
    ALL: `${API_PREFIX}/patients/all`,
    PUSULA_PATIENT: (protocolNo) => `${API_PREFIX}/pusula/patient/${protocolNo}`,
    PUSULA_PATIENT_BY_NO: (patientNo) => `${API_PREFIX}/pusula/patient-by-no/${patientNo}`,
    PUSULA_DIAGNOSES_PATIENTS: (icdCode) => `${API_PREFIX}/pusula/diagnoses/${icdCode}/patients`,
    PUSULA_VISITS: (patientId) => `${API_PREFIX}/pusula/patients/${patientId}/visits`,
    PUSULA_VISITS_BULK: (patientId) => `${API_PREFIX}/pusula/patients/${patientId}/visits/bulk`,
    PUSULA_VISIT: (patientId, visitId) => `${API_PREFIX}/pusula/patients/${patientId}/visits/${visitId}`,
    EXPORT_EXCEL: `${API_PREFIX}/patients/export/excel`,
    EXPORT_SCHEMA: `${API_PREFIX}/patients/export/schema`,
    EXPORT_ADVANCED_EXCEL: `${API_PREFIX}/patients/export/advanced-excel`,
    TENANT_ALL: `${API_PREFIX}/patients/tenant/all`,
    TENANT_STATS: `${API_PREFIX}/patients/tenant/stats`,
    TENANT_EXPORT_EXCEL: `${API_PREFIX}/patients/tenant/export/excel`,
    BY_ID: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    DELETE: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    UPDATE: (patientId) => `${API_PREFIX}/patients/${patientId}`,
    INVITATIONS: (patientId) => `${API_PREFIX}/patients/${patientId}/invitations`,
    UPLOAD_FILES: (patientId) => `${API_PREFIX}/patients/${patientId}/upload-files`,
    UPLOAD_FILES_MULTIPLE: (patientId) => `${API_PREFIX}/patients/${patientId}/upload-files/multiple`,
    UPLOAD_FILE_BY_ID: (patientId, fileId) => `${API_PREFIX}/patients/${patientId}/upload-files/${fileId}`,
    DOWNLOAD_FILE: (patientId, fileId) => `${API_PREFIX}/patients/${patientId}/upload-files/${fileId}/download`,
    DYNAMIC_FIELD_CATALOG: `${API_PREFIX}/patients/dynamic-field-catalog`,
    PUSULA_LAB_FILTER_OPTIONS: `${API_PREFIX}/patients/pusula-lab-filter-options`,
    DYNAMIC_FIELD_CATALOG_CHART: `${API_PREFIX}/patients/dynamic-field-catalog/chart`,
    DYNAMIC_FIELD_CHART_STATS: `${API_PREFIX}/patients/dynamic-field-chart-stats`,
    FIELD_RELATIONSHIP_CHART: `${API_PREFIX}/patients/field-relationship-chart`,
    EXPORT_FILES_EXCEL: (patientId) => `${API_PREFIX}/patients/${patientId}/export/files/excel`,
    EXPORT_INVITATIONS_EXCEL: (patientId) => `${API_PREFIX}/patients/${patientId}/export/invitations/excel`,
    EXPORT_FORM_ANSWERS_EXCEL: (patientId) => `${API_PREFIX}/patients/${patientId}/export/form-answers/excel`,
    EXPORT_UPLOAD_FILES_EXCEL: (patientId) => `${API_PREFIX}/patients/${patientId}/export/upload-files/excel`,
    TENANT_EXPORT_FILES_EXCEL: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}/export/files/excel`,
    TENANT_EXPORT_INVITATIONS_EXCEL: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}/export/invitations/excel`,
    TENANT_EXPORT_FORM_ANSWERS_EXCEL: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}/export/form-answers/excel`,
    TENANT_EXPORT_UPLOAD_FILES_EXCEL: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}/export/upload-files/excel`,
  },

  // Doctor endpoints
  DOCTORS: {
    SUGGESTIONS: `${API_PREFIX}/doctors/suggestions`,
    BY_ID: (doctorId) => `${API_PREFIX}/doctors/${doctorId}`,
    UPDATE: (doctorId) => `${API_PREFIX}/doctors/${doctorId}`,
    ALL: `${API_PREFIX}/doctors/all`,
    EXPORT_EXCEL: `${API_PREFIX}/doctors/export/excel`,
    TENANT_ALL: `${API_PREFIX}/doctors/tenant/all`,
    TENANT_EXPORT_EXCEL: `${API_PREFIX}/doctors/tenant/export/excel`,
    TENANT_BY_ID: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
    TENANT_ALLOCATE_AI_TOKENS: (doctorId) => `${API_PREFIX}/doctors/${doctorId}/ai-tokens`,
    TENANT_UPDATE: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
    TENANT_DELETE: (doctorId) => `${API_PREFIX}/doctors/tenant/${doctorId}`,
  },

  // Tenant endpoints
  TENANTS: {
    GET_ALL_FOR_TENANT: `${API_PREFIX}/tenants`,
    UPDATE: (tenantId) => `${API_PREFIX}/tenants/${tenantId}`,
    DELETE: (tenantId) => `${API_PREFIX}/tenants/${tenantId}`,
    BY_ID: (tenantId) => `${API_PREFIX}/tenants/${tenantId}`,
    DASHBOARD_STATS: `${API_PREFIX}/tenants/dashboard/stats`,
    PATIENT_BY_ID: (patientId) => `${API_PREFIX}/patients/tenant/${patientId}`,
  },

  // Form endpoints
  FORMS: {
    CREATE: `${API_PREFIX}/forms`,
    ALL: `${API_PREFIX}/forms`,
    BY_ID: (formId) => `${API_PREFIX}/forms/${formId}`,
    UPDATE: (formId) => `${API_PREFIX}/forms/${formId}`,
    DELETE: (formId) => `${API_PREFIX}/forms/${formId}`,
    INVITATIONS: (formId) => `${API_PREFIX}/forms/${formId}/invitations`,
    EXPORT_PATIENTS_EXCEL: (formId) => `${API_PREFIX}/forms/${formId}/export/patients/excel`,
    AGE_SCORE_SCATTER: (formId) => `${API_PREFIX}/forms/${formId}/scatter/age-score`,
    INSIGHTS_AI_SUMMARY: (formId) =>
      `${API_PREFIX}/forms/${formId}/insights/ai-summary`,
    INSIGHTS_AI_SUMMARY_PATIENT: (formId, patientId) =>
      `${API_PREFIX}/forms/${formId}/insights/ai-summary/patients/${patientId}`,
    KIOSK_ELIGIBILITY: (formId) => `${API_PREFIX}/forms/${formId}/kiosk-eligibility`,
    TENANT_EXPORT_PATIENTS_EXCEL: (formId) => `${API_PREFIX}/forms/tenant/${formId}/export/patients/excel`,
  },

  // Form Answers endpoints
  FORM_ANSWERS: {
    CREATE: `${API_PREFIX}/form-answers`,
    ALL: `${API_PREFIX}/form-answers`,
    BY_ID: (formId) => `${API_PREFIX}/form-answers/${formId}`,
    EXPORT_EXCEL: (formId) => `${API_PREFIX}/form-answers/${formId}/export/excel`,
  },

  // Public Form endpoints
  PUBLIC: {
    FORM: (formToken) => `${API_PREFIX}/public/forms/${formToken}`,
    FORM_ANSWER: `${API_PREFIX}/public/form-answers`,
  },

  // File endpoints
  FILES: {
    SUGGESTIONS: `${API_PREFIX}/files/suggestions`,
    CREATE: `${API_PREFIX}/files`,
    ALL: `${API_PREFIX}/files`,
    EXPORT_EXCEL: `${API_PREFIX}/files/export/excel`,
    BY_ID: (fileId) => `${API_PREFIX}/files/${fileId}`,
    UPDATE: (fileId) => `${API_PREFIX}/files/${fileId}`,
    DELETE: (fileId) => `${API_PREFIX}/files/${fileId}`,
    COPY: (fileId) => `${API_PREFIX}/files/${fileId}/copy`,
    EXPORT_FORMS_EXCEL: (fileId) => `${API_PREFIX}/files/${fileId}/export/forms/excel`,
    EXPORT_PATIENTS_EXCEL: (fileId) => `${API_PREFIX}/files/${fileId}/export/patients/excel`,
    TENANT_EXPORT_FORMS_EXCEL: (fileId) => `${API_PREFIX}/files/tenant/${fileId}/export/forms/excel`,
    TENANT_EXPORT_PATIENTS_EXCEL: (fileId) => `${API_PREFIX}/files/tenant/${fileId}/export/patients/excel`,
  },

  // MR (Medical Record) endpoints
  MR: {
    UPLOAD: `${API_PREFIX}/mr/upload`,
    BY_PATIENT: (patientId) => `${API_PREFIX}/mr/patient/${patientId}`,
    ANALYZE: (mrId, segmentType) => `${API_PREFIX}/mr/${mrId}/analyze?segmentType=${segmentType}`,
  },

  // Scoring Formula endpoints
  SCORING_FORMULAS: {
    CREATE: `${API_PREFIX}/scoring-formulas`,
    ALL: `${API_PREFIX}/scoring-formulas`,
    EXPORT_EXCEL: `${API_PREFIX}/scoring-formulas/export/excel`,
    BY_ID: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    UPDATE: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    DELETE: (formulaId) => `${API_PREFIX}/scoring-formulas/${formulaId}`,
    TEST: `${API_PREFIX}/scoring-formulas/test`,
  },

  // Permission Groups endpoints
  PERMISSION_GROUPS: {
    // Tenant routes (full access)
    ALL: `${API_PREFIX}/permission-groups`,
    BY_ID: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    CREATE: `${API_PREFIX}/permission-groups`,
    UPDATE: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    DELETE: (groupId) => `${API_PREFIX}/permission-groups/${groupId}`,
    
    // Admin doctor routes (with permission checks)
    ADMIN: {
      ALL: `${API_PREFIX}/permission-groups/admin/all`,
      BY_ID: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
      CREATE: `${API_PREFIX}/permission-groups/admin`,
      UPDATE: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
      DELETE: (groupId) => `${API_PREFIX}/permission-groups/admin/${groupId}`,
    },
    
    // Doctor assignment routes
    DOCTOR_GROUPS: (doctorId) => `${API_PREFIX}/permission-groups/doctor/${doctorId}`,
    ASSIGN_DOCTOR: `${API_PREFIX}/permission-groups/assign-doctor`,
    REMOVE_DOCTOR: (doctorId, groupId) => `${API_PREFIX}/permission-groups/remove-doctor/${doctorId}/${groupId}`,
  },

  // Collaboration Groups endpoints
  COLLABORATION_GROUPS: {
    ALL: `${API_PREFIX}/collaboration-groups`,
    MY: `${API_PREFIX}/collaboration-groups/my`,
    ELIGIBLE_DOCTORS: `${API_PREFIX}/collaboration-groups/eligible-doctors`,
    BY_ID: (groupId) => `${API_PREFIX}/collaboration-groups/${groupId}`,
    CREATE: `${API_PREFIX}/collaboration-groups`,
    UPDATE: (groupId) => `${API_PREFIX}/collaboration-groups/${groupId}`,
    DELETE: (groupId) => `${API_PREFIX}/collaboration-groups/${groupId}`,
    LINK_FILE: (groupId) => `${API_PREFIX}/collaboration-groups/${groupId}/files`,
    UPDATE_FILE_LINK: (groupId, fileId) =>
      `${API_PREFIX}/collaboration-groups/${groupId}/files/${fileId}`,
    UNLINK_FILE: (groupId, fileId) =>
      `${API_PREFIX}/collaboration-groups/${groupId}/files/${fileId}`,
  },

  // Form Market (clinical form templates catalog)
  FORM_MARKET: {
    SPECIALTIES: `${API_PREFIX}/form-market/specialties`,
    TEMPLATES: `${API_PREFIX}/form-market/templates`,
    TEMPLATE_BY_SLUG: (slug) => `${API_PREFIX}/form-market/templates/${slug}`,
  },

  // Statistical tests (biostat wizard)
  STATISTICAL_TESTS: {
    VARIABLES: `${API_PREFIX}/statistical-tests/variables`,
    PROFILE_DATASET: `${API_PREFIX}/statistical-tests/profile-dataset`,
    RUN: `${API_PREFIX}/statistical-tests/run`,
    FEATURE_IMPACT: `${API_PREFIX}/statistical-tests/feature-impact`,
    FEATURE_IMPACT_SCOPE: `${API_PREFIX}/statistical-tests/feature-impact/scope`,
    CLINICAL_HARMONIZATION_PREPARED: `${API_PREFIX}/statistical-tests/clinical-harmonization/prepared`,
    COHORT_SCOPE: `${API_PREFIX}/statistical-tests/cohort-scope`,
  },
  MACHINE_LEARNING: {
    RUN: `${API_PREFIX}/machine-learning/run`,
    ESTIMATE: `${API_PREFIX}/machine-learning/estimate`,
    CREDITS: `${API_PREFIX}/machine-learning/credits`,
    CREDIT_HISTORY: `${API_PREFIX}/machine-learning/credits/history`,
    RECOMMENDATIONS: `${API_PREFIX}/machine-learning/recommendations`,
    JOBS: `${API_PREFIX}/machine-learning/jobs`,
    JOB: (jobId) => `${API_PREFIX}/machine-learning/jobs/${jobId}`,
    EXPLANATIONS: (jobId) => `${API_PREFIX}/machine-learning/jobs/${jobId}/explanations`,
    EXPLANATIONS_ESTIMATE: (jobId) => `${API_PREFIX}/machine-learning/jobs/${jobId}/explanations/estimate`,
    COHORT_PREVIEW: `${API_PREFIX}/machine-learning/cohort-preview`,
    // ML Projeleri hub'ı (Gözlemci Çalışmaları karşılığı)
    PROJECTS: `${API_PREFIX}/machine-learning/projects`,
    PROJECT: (id) => `${API_PREFIX}/machine-learning/projects/${id}`,
    PROJECT_JOBS: (id) => `${API_PREFIX}/machine-learning/projects/${id}/jobs`,
    PROJECT_JOB: (id, jobId) => `${API_PREFIX}/machine-learning/projects/${id}/jobs/${jobId}`,
    PROJECT_ARCHIVE: (id) => `${API_PREFIX}/machine-learning/projects/${id}/archive`,
    PROJECT_UNARCHIVE: (id) => `${API_PREFIX}/machine-learning/projects/${id}/unarchive`,
    PROJECT_DUPLICATE: (id) => `${API_PREFIX}/machine-learning/projects/${id}/duplicate`,
    // ML Faz 4/7: proje kohort önizlemesi (snapshot projesinde sunucu snapshot'ı çözer).
    PROJECT_COHORT_PREVIEW: (id) => `${API_PREFIX}/machine-learning/projects/${id}/cohort-preview`,
    SHARE_CANDIDATES: `${API_PREFIX}/machine-learning/share-candidates`,
    PROJECT_SHARES: (id) => `${API_PREFIX}/machine-learning/projects/${id}/shares`,
    PROJECT_SHARE: (id, grantId) => `${API_PREFIX}/machine-learning/projects/${id}/shares/${grantId}`,
    PROJECT_SHARE_ARCHIVE: (id) => `${API_PREFIX}/machine-learning/projects/${id}/shares/me/archive`,
    PROJECT_SHARE_UNARCHIVE: (id) => `${API_PREFIX}/machine-learning/projects/${id}/shares/me/unarchive`,
    PROJECT_VARIABLES: (id) => `${API_PREFIX}/machine-learning/projects/${id}/variables`,
    PROJECT_PROFILE_DATASET: (id) => `${API_PREFIX}/machine-learning/projects/${id}/profile-dataset`,
    PROJECT_PROFILE_JOB: (id, jobId) => `${API_PREFIX}/machine-learning/projects/${id}/profile-job/${jobId}`,
    PROJECT_COMPARISONS: (id) => `${API_PREFIX}/machine-learning/projects/${id}/comparisons`,
    PROJECT_COMPARISON: (id, comparisonId) => `${API_PREFIX}/machine-learning/projects/${id}/comparisons/${comparisonId}`,
    PROJECT_FEATURE_SETS: (id) => `${API_PREFIX}/machine-learning/projects/${id}/feature-sets`,
    PROJECT_FEATURE_SET: (id, featureSetId) => `${API_PREFIX}/machine-learning/projects/${id}/feature-sets/${featureSetId}`,
    PROJECT_FEATURE_SET_NAME: (id, featureSetId) => `${API_PREFIX}/machine-learning/projects/${id}/feature-sets/${featureSetId}/name`,
  },

  // Stats endpoints
  STATS: {
    DOCTOR: `${API_PREFIX}/stats/doctor`,
    DOCTOR_EXPORT_EXCEL: `${API_PREFIX}/stats/doctor/export/excel`,
    TENANT: `${API_PREFIX}/stats/tenant`,
    TENANT_EXPORT_EXCEL: `${API_PREFIX}/stats/tenant/export/excel`,
    SUPER_ADMIN: `${API_PREFIX}/stats/super-admin`,
    SUPER_ADMIN_EXPORT_EXCEL: `${API_PREFIX}/stats/super-admin/export/excel`,
    GENERAL: `${API_PREFIX}/stats`,
    EXPORT_EXCEL: `${API_PREFIX}/stats/export/excel`,
  },

  // Stats job & quota (hibrit hesaplama sistemi)
  STATS_JOBS: {
    ESTIMATE:      `${API_PREFIX}/stats/estimate`,
    LIST:          `${API_PREFIX}/stats/jobs`,
    JOB:           (jobId) => `${API_PREFIX}/stats/jobs/${jobId}`,
    QUOTA:         `${API_PREFIX}/stats/quota`,
    USAGE_HISTORY: `${API_PREFIX}/stats/usage-history`,
    SUPER_ADMIN: {
      PLANS: `${API_PREFIX}/super-admin/stats/plans`,
      UPDATE_PLAN: (planId) => `${API_PREFIX}/super-admin/stats/plans/${planId}`,
      TENANT_ADDON: (tenantId) => `${API_PREFIX}/super-admin/stats/tenants/${tenantId}/addon`,
      TENANT_USAGE: (tenantId) => `${API_PREFIX}/super-admin/stats/tenants/${tenantId}/usage`,
    }
  },

  EXCEL_IMPORT: {
    PREVIEW: `${API_PREFIX}/excel-import/preview`,
    TEMPLATES: `${API_PREFIX}/excel-import/templates`,
    TEMPLATE_BY_ID: (id) => `${API_PREFIX}/excel-import/templates/${id}`,
    DRY_RUN: `${API_PREFIX}/excel-import/dry-run`,
    EXECUTE: `${API_PREFIX}/excel-import/execute`,
    JOBS: `${API_PREFIX}/excel-import/jobs`,
  },

  DATA_WORKSPACE: {
    DATASET: (id) => `${API_PREFIX}/data-workspace/${id}`,
    ROW_LOCATION: (id) => `${API_PREFIX}/data-workspace/${id}/rows/location`,
    METADATA: (id) => `${API_PREFIX}/data-workspace/${id}/metadata`,
    PROFILE: (id) => `${API_PREFIX}/data-workspace/${id}/profile`,
    COLUMN_PROFILE: (id, columnKey) => `${API_PREFIX}/data-workspace/${id}/columns/${encodeURIComponent(columnKey)}/profile`,
    CELLS: (id) => `${API_PREFIX}/data-workspace/${id}/cells`,
    VARIABLES: (id, column) => `${API_PREFIX}/data-workspace/${id}/variables/${encodeURIComponent(column)}`,
    UNDO: (id) => `${API_PREFIX}/data-workspace/${id}/undo`,
    REDO: (id) => `${API_PREFIX}/data-workspace/${id}/redo`,
    FILTERS: (id) => `${API_PREFIX}/data-workspace/${id}/filters`,
    ANALYSIS_READY: (id) => `${API_PREFIX}/data-workspace/${id}/analysis-ready`,
    TRANSFORMATIONS: (id) => `${API_PREFIX}/data-workspace/${id}/transformations`,
    TRANSFORMATION_BY_ID: (id, trId) => `${API_PREFIX}/data-workspace/${id}/transformations/${encodeURIComponent(trId)}`,
    TRANSFORMATION_PREVIEW: (id) => `${API_PREFIX}/data-workspace/${id}/transformations/preview`,
    MISSING_OVERVIEW: (id) => `${API_PREFIX}/data-workspace/${id}/missing/overview`,
    CONVERT_MISSING_CODE: (id) => `${API_PREFIX}/data-workspace/${id}/missing/convert-code`,
    SET_MISSING_REASON: (id) => `${API_PREFIX}/data-workspace/${id}/missing/reason`,
    COMPLETE_CASE_PREVIEW: (id) => `${API_PREFIX}/data-workspace/${id}/missing/complete-case-preview`,
    QUALITY_OVERVIEW: (id) => `${API_PREFIX}/data-workspace/${id}/quality/overview`,
    RESOLVE_QUALITY_ISSUE: (id) => `${API_PREFIX}/data-workspace/${id}/quality/resolve`,
    SEMANTIC_MAPPINGS: (id) => `${API_PREFIX}/data-workspace/${id}/quality/semantic-mappings`,
    SEMANTIC_MAPPINGS_CLEAR: (id) => `${API_PREFIX}/data-workspace/${id}/quality/semantic-mappings/clear`,
    CUSTOM_RULES: (id) => `${API_PREFIX}/data-workspace/${id}/quality/custom-rules`,
    CUSTOM_RULE: (id, ruleId) => `${API_PREFIX}/data-workspace/${id}/quality/custom-rules/${encodeURIComponent(ruleId)}`,
    // Phase 4: Versioning, Reproducibility, Lineage, Export, AI Assistant
    VERSIONS: (id) => `${API_PREFIX}/data-workspace/${id}/versions`,
    VERSION_BY_ID: (id, vId) => `${API_PREFIX}/data-workspace/${id}/versions/${vId}`,
    RESTORE_VERSION: (id, vId) => `${API_PREFIX}/data-workspace/${id}/versions/${encodeURIComponent(vId)}/restore`,
    RENAME_DATASET: (id) => `${API_PREFIX}/data-workspace/${id}/name`,
    FORK_DATASET: (id) => `${API_PREFIX}/data-workspace/${id}/fork`,
    DIFF: (id) => `${API_PREFIX}/data-workspace/${id}/diff`,
    AUDIT_HISTORY: (id) => `${API_PREFIX}/data-workspace/${id}/history`,
    LINEAGES: (id) => `${API_PREFIX}/data-workspace/${id}/analyses`,
    LINEAGE_BY_ID: (id, lId) => `${API_PREFIX}/data-workspace/${id}/analyses/${lId}`,
    LINEAGE_MANIFEST: (id, lId) => `${API_PREFIX}/data-workspace/${id}/analyses/${lId}/manifest`,
    LINEAGE_RERUN: (id, lId) => `${API_PREFIX}/data-workspace/${id}/analyses/${lId}/rerun`,
    EXPORT_EXCEL: (id) => `${API_PREFIX}/data-workspace/${id}/export/excel`,
    EXPORT_CSV: (id) => `${API_PREFIX}/data-workspace/${id}/export/csv`,
    DATA_DICTIONARY: (id) => `${API_PREFIX}/data-workspace/${id}/export/data-dictionary`,
    TRANSFORMATION_LOG: (id) => `${API_PREFIX}/data-workspace/${id}/export/transformation-log`,
    AI_ASSISTANT_PROMPT: (id) => `${API_PREFIX}/data-workspace/${id}/assistant/prompt`,
    AI_ASSISTANT_APPLY: (id) => `${API_PREFIX}/data-workspace/${id}/assistant/apply`,
    // Temizlik altyapısı (Temizlik Faz 0): önizleme → onay → atomik uygulama
    CLEANING_CAPABILITIES: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/capabilities`,
    CLEANING_SUGGESTIONS: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/suggestions`,
    CLEANING_PREVIEW: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/preview`,
    CLEANING_APPLY: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/apply`,
    CLEANING_JOBS: `${API_PREFIX}/data-workspace/cleaning/jobs`,
    CLEANING_JOB: (jobId) => `${API_PREFIX}/data-workspace/cleaning/jobs/${jobId}`,
    CLEANING_ACTIVE_JOB: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/active-job`,
    // Temizlik Faz 5: salt okunur harmonizasyon/örüntü + kişisel tarif kütüphanesi (hücre yazmaz)
    CLEANING_HARMONIZATION: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/harmonization`,
    CLEANING_CONDITIONAL_PATTERNS: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/conditional-patterns`,
    CLEANING_RECIPE_SOURCES: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/recipe-sources`,
    CLEANING_RECIPES: (id) => `${API_PREFIX}/data-workspace/${id}/cleaning/recipes`,
    CLEANING_RECIPE: (id, recipeId) => `${API_PREFIX}/data-workspace/${id}/cleaning/recipes/${recipeId}`,
    CLEANING_RECIPE_COMPATIBILITY: (id, recipeId) => `${API_PREFIX}/data-workspace/${id}/cleaning/recipes/${recipeId}/compatibility`,
  },

  DEMO: {
    COHORT: `${API_PREFIX}/demo/cohort`,
  },

  // Subscription / plans / quotas endpoints
  SUBSCRIPTION: {
    CATALOG: `${API_PREFIX}/subscription/catalog`,
    PLANS: `${API_PREFIX}/subscription/plans`,
    CURRENT: `${API_PREFIX}/subscription/current`,
    ORDERS: `${API_PREFIX}/subscription/orders`,
    USAGE: `${API_PREFIX}/subscription/usage`,
    CHECKOUT_SIMULATE: `${API_PREFIX}/subscription/checkout-simulate`,
    CHANGE_SIMULATE: `${API_PREFIX}/subscription/change-simulate`,
    TENANT_NOTIFICATIONS: `${API_PREFIX}/subscription/tenant-notifications`,
    TENANT_NOTIFICATION_READ: (id) => `${API_PREFIX}/subscription/tenant-notifications/${id}/read`,

    SUPER_ADMIN: {
      PLANS: `${API_PREFIX}/subscription/super-admin/plans`,
      PLAN_UPSERT: (code) => `${API_PREFIX}/subscription/super-admin/plans/${code}`,
      BILLING_SETTINGS: `${API_PREFIX}/subscription/super-admin/billing-settings`,
      TENANT_CURRENT: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/current`,
      TENANT_USAGE: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/usage`,
      TENANT_ORDERS: (tenantId) => `${API_PREFIX}/subscription/super-admin/tenants/${tenantId}/orders`,
    },
  },

  // Notifications (doctor / tenant)
  NOTIFICATIONS: {
    LIST: `${API_PREFIX}/notifications`,
    STATS: `${API_PREFIX}/notifications/stats`,
    READ: (id) => `${API_PREFIX}/notifications/${id}/read`,
    READ_ALL: `${API_PREFIX}/notifications/read-all`,
    DELETE: (id) => `${API_PREFIX}/notifications/${id}`,
    DELETE_ALL: `${API_PREFIX}/notifications/all`,
  },

  // Super Admin endpoints
  SUPER_ADMIN: {
    LOGIN: `${API_PREFIX}/auth/super-admin/login`,
    STATS: `${API_PREFIX}/super-admin/stats`,
    WEEKLY_STATS: `${API_PREFIX}/super-admin/stats/weekly`,
    TENANTS: {
      ALL: `${API_PREFIX}/super-admin/tenants`,
      BY_ID: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}`,
      DELETE: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}`,
      UPDATE_STATUS: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}/active`,
      UPDATE_EXCEL_EXPORT: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}/excel-export`,
      UPDATE_PUSULA_INTEGRATION: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}/pusula`,
      ADD_AI_TOKENS: (tenantId) => `${API_PREFIX}/super-admin/tenants/${tenantId}/ai-tokens`,
    },
    DOCTORS: {
      ALL: `${API_PREFIX}/super-admin/doctors`,
      BY_ID: (doctorId) => `${API_PREFIX}/super-admin/doctors/${doctorId}`,
      DELETE: (doctorId) => `${API_PREFIX}/super-admin/doctors/${doctorId}`,
    },
    PATIENTS: {
      ALL: `${API_PREFIX}/super-admin/patients`,
      BY_ID: (patientId) => `${API_PREFIX}/super-admin/patients/${patientId}`,
      DELETE: (patientId) => `${API_PREFIX}/super-admin/patients/${patientId}`,
    },
    FORMS: {
      ALL: `${API_PREFIX}/super-admin/forms`,
      DELETE: (formId) => `${API_PREFIX}/super-admin/forms/${formId}`,
    },
    FORM_ANSWERS: {
      ALL: `${API_PREFIX}/super-admin/form-answers`,
      DELETE: (answerId) => `${API_PREFIX}/super-admin/form-answers/${answerId}`,
    },
    FORM_INVITATIONS: {
      ALL: `${API_PREFIX}/super-admin/form-invitations`,
      DELETE: (invitationId) => `${API_PREFIX}/super-admin/form-invitations/${invitationId}`,
    },
    SCORING_FORMULAS: {
      ALL: `${API_PREFIX}/super-admin/scoring-formulas`,
      DELETE: (formulaId) => `${API_PREFIX}/super-admin/scoring-formulas/${formulaId}`,
    },
    FILES: {
      ALL: `${API_PREFIX}/super-admin/files`,
      BY_ID: (fileId) => `${API_PREFIX}/super-admin/files/${fileId}`,
      DELETE: (fileId) => `${API_PREFIX}/super-admin/files/${fileId}`,
    },
    MRS: {
      ALL: `${API_PREFIX}/super-admin/mrs`,
      DELETE: (mrId) => `${API_PREFIX}/super-admin/mrs/${mrId}`,
    },
    DOCTOR_INVITATIONS: {
      ALL: `${API_PREFIX}/super-admin/doctor-invitations`,
      DELETE: (invitationId) => `${API_PREFIX}/super-admin/doctor-invitations/${invitationId}`,
    },
    PASSWORD_RESETS: {
      ALL: `${API_PREFIX}/super-admin/password-resets`,
      DELETE: (resetId) => `${API_PREFIX}/super-admin/password-resets/${resetId}`,
    },
    TRASH_LOGS: {
      ALL: `${API_PREFIX}/super-admin/trash-logs`,
      BY_ID: (logId) => `${API_PREFIX}/super-admin/trash-logs/${logId}`,
      REQUEST_OTP: `${API_PREFIX}/super-admin/rollback/request-otp`,
      ROLLBACK: `${API_PREFIX}/super-admin/rollback`,
    },
    EXPORTS: {
      RESOURCES: `${API_PREFIX}/super-admin/exports/resources`,
      EXCEL: `${API_PREFIX}/super-admin/exports/excel`,
    },

    // Log ve Sistem İzleme Modülü
    MONITORING: {
      DASHBOARD: `${API_PREFIX}/super-admin/monitoring/dashboard`,
      FILTER_OPTIONS: `${API_PREFIX}/super-admin/monitoring/filters/options`,
      SETTINGS: `${API_PREFIX}/super-admin/monitoring/settings`,
      AUDIT_LOGS: {
        ALL: `${API_PREFIX}/super-admin/monitoring/audit-logs`,
        BY_ID: (logId) => `${API_PREFIX}/super-admin/monitoring/audit-logs/${logId}`,
        EXPORT: `${API_PREFIX}/super-admin/monitoring/audit-logs/export`,
      },
      ERROR_LOGS: {
        ALL: `${API_PREFIX}/super-admin/monitoring/error-logs`,
        BY_ID: (logId) => `${API_PREFIX}/super-admin/monitoring/error-logs/${logId}`,
        EXPORT: `${API_PREFIX}/super-admin/monitoring/error-logs/export`,
      },
    },
  },
  // MediaVision endpoints
  MEDIAVISION: {
    QUICK_UPLOAD: `${API_PREFIX}/mediavision/quick-measure/upload`,
    QUICK_SAVE: `${API_PREFIX}/mediavision/quick-measure/save`,
    QUICK_HISTORY: `${API_PREFIX}/mediavision/quick-measure/history`,
    QUICK_IMAGE: (imageId) => `${API_PREFIX}/mediavision/quick-measure/images/${imageId}`,
    QUICK_IMAGE_CONTENT: (imageId) => `${API_PREFIX}/mediavision/quick-measure/images/${imageId}/content`,
    PATIENT_MEASUREMENTS: (patientId) => `${API_PREFIX}/mediavision/patient/${patientId}`,
    IMAGE_DETAILS: (imageId) => `${API_PREFIX}/mediavision/image/${imageId}`,
    DOCTOR_MEASUREMENTS: `${API_PREFIX}/mediavision/doctor/measurements`,
    IMAGE_FILE: (imageId) => `${API_PREFIX}/mediavision/image-file/${imageId}`,
    DELETE_IMAGE: (imageId) => `${API_PREFIX}/mediavision/image/${imageId}`,
    TEMPLATES: `${API_PREFIX}/mediavision/templates`,
    TEMPLATE: (templateId) => `${API_PREFIX}/mediavision/templates/${templateId}`,

    // Observer study slice — public product prefix of the clinical measurement
    // module. Identifiers are encoded because a study id can reach the client
    // as free user input (invitation code paste).
    STUDIES: `${API_PREFIX}/mediavision/studies`,
    STUDIES_ASSIGNED: `${API_PREFIX}/mediavision/studies/assigned`,
    STUDY_OBSERVER_OVERVIEW: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${studyId}/observer-overview`,
    STUDY: (studyId) => `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}`,
    STUDY_CURRENT_ASSIGNMENT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/assignments/current`,
    SESSION_WORKBENCH: (studyId, sessionId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/sessions/${encodeURIComponent(sessionId)}/workbench`,
    SESSION_IMAGE: (studyId, sessionId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/sessions/${encodeURIComponent(sessionId)}/image`,
    SESSION_DRAFT: (studyId, sessionId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/sessions/${encodeURIComponent(sessionId)}/draft`,
    SESSION_SUBMIT: (studyId, sessionId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/sessions/${encodeURIComponent(sessionId)}/submit`,
    STUDY_ANALYZE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/analyze`,
    STUDY_ANALYSIS_RESULTS: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/analysis-results`,
    STUDY_ANALYSIS_RESULT_DETAIL: (studyId, resultId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/analysis-results/${encodeURIComponent(resultId)}`,

    // Observer study manager (admin) — shares the /studies prefix, mounted
    // behind the same authMiddleware. Bkz. measurementStudyAdminRoutes.js.
    STUDY_PROTOCOLS: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/protocols`,
    STUDY_PROTOCOL_ACTIVATE: (studyId, protocolId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/protocols/${encodeURIComponent(protocolId)}/activate`,
    STUDY_CASES: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/cases`,
    STUDY_CASE: (studyId, caseId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/cases/${encodeURIComponent(caseId)}`,
    STUDY_OBSERVERS: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/observers`,
    STUDY_OBSERVER: (studyId, observerId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/observers/${encodeURIComponent(observerId)}`,
    STUDY_ACTIVATION_PREFLIGHT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/activation/preflight`,
    STUDY_ACTIVATE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/activate`,
    STUDY_ADVANCE_ROUND: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/advance-round`,
    // Observer-bazlı round state machine (Aşama 10 BE contract'ı, Aşama 11 FE tüketimi).
    STUDY_ROUNDS: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/rounds`,
    STUDY_SCHEDULE_ROUND: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/schedule-round`,
    STUDY_ACCEPT_INVITATION: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/accept-invitation`,
    STUDY_ARCHIVE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/archive`,
    STUDY_UNARCHIVE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/unarchive`,
    STUDY_DELETE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}`,
    STUDY_LIST_ARCHIVE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/list-archive`,
    STUDY_LIST_UNARCHIVE: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/list-unarchive`,
    STUDY_LOCK: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/lock`,
    STUDY_EXPORT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/export`,
    RETROSPECTIVE_IMPORT_TEMPLATE: (studyId, layout) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-import/template?layout=${encodeURIComponent(layout)}`,
    RETROSPECTIVE_IMPORT_INSPECT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-import/inspect`,
    RETROSPECTIVE_IMPORT_PREVIEW: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-import/preview`,
    RETROSPECTIVE_IMPORT_COMMIT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-import/commit`,
    RETROSPECTIVE_IMPORT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-import`,
    RETROSPECTIVE_ANNOTATION_IMPORT_PREVIEW: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-annotation-import/preview`,
    RETROSPECTIVE_ANNOTATION_IMPORT_COMMIT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-annotation-import/commit`,
    RETROSPECTIVE_ANNOTATION_IMPORT: (studyId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/retrospective-annotation-import`,
    STUDY_RESEND_INVITATION: (studyId, observerId) =>
      `${API_PREFIX}/mediavision/studies/${encodeURIComponent(studyId)}/observers/${encodeURIComponent(observerId)}/resend-invite`,

    // Dataset Management (Phase 6)
    DATASETS: {
      PROJECTS: `${API_PREFIX}/mediavision/datasets/projects`,
      PROJECT: (id) => `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(id)}`,
      CLASSES: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/classes`,
      CLASSES_REORDER: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/classes/reorder`,
      CLASS: (projectId, classId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/classes/${encodeURIComponent(classId)}`,
      IMAGES: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images`,
      IMAGES_BATCH: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images`,
      IMAGES_ZIP: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/zip`,
      IMAGE: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}`,
      IMAGE_CONTENT: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/content`,
      IMAGES_ENCRYPTION_ENVELOPES_BATCH: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/encryption-envelopes/batch`,
      IMAGE_REVIEW_STATUS: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/review-status`,
      EXPORT: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/export`,
      EXPORT_REQUESTS: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/export-requests`,
      EXPORT_REQUEST_APPROVE: (requestId) =>
        `${API_PREFIX}/mediavision/datasets/export-requests/${encodeURIComponent(requestId)}/approve`,
      EXPORT_REQUEST_REJECT: (requestId) =>
        `${API_PREFIX}/mediavision/datasets/export-requests/${encodeURIComponent(requestId)}/reject`,
      EXPORT_REQUEST_CANCEL: (requestId) =>
        `${API_PREFIX}/mediavision/datasets/export-requests/${encodeURIComponent(requestId)}/cancel`,
      EXPORT_REQUEST_TOKEN: (requestId) =>
        `${API_PREFIX}/mediavision/datasets/export-requests/${encodeURIComponent(requestId)}/download-token`,
      EXPORT_DOWNLOAD: (token) =>
        `${API_PREFIX}/mediavision/datasets/export-download/${encodeURIComponent(token)}`,
      // Image-level classification export (Phase 9J-Faz2A) — separate from geometry export.
      CLASSIFICATION_EXPORT: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/export/classification`,
      ANNOTATIONS: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/annotations`,
      LABELME_IMPORT: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/import-labelme`,
      YOLO_IMPORT: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/import-yolo`,
      ANNOTATION_DETAIL: (projectId, imageId, annotationId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/annotations/${encodeURIComponent(annotationId)}`,
      // Image-level classification labels (task_type=classification, Phase 9J)
      IMAGE_LABELS: (projectId, imageId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/labels`,
      IMAGE_LABEL_DETAIL: (projectId, imageId, classId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/images/${encodeURIComponent(imageId)}/labels/${encodeURIComponent(classId)}`,
      LABELS_BULK: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/labels/bulk`,
      CLASSIFICATION_STATS: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/classification-stats`,
      // Classification ZIP import preview + atomic commit (Phase 9J-Faz2B)
      CLASSIFICATION_IMPORT_PREVIEW: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/import/classification/preview`,
      CLASSIFICATION_IMPORT_COMMIT: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/import/classification/commit`,
      // Dataset sharing (Faz 4) — mounted under measurementDatasetAdminRoutes
      SHARE_CANDIDATES: `${API_PREFIX}/mediavision/datasets/share-candidates`,
      SHARES: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/shares`,
      SHARE: (projectId, grantId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/shares/${encodeURIComponent(grantId)}`,
      EFFECTIVE_PERMISSIONS: (projectId) =>
        `${API_PREFIX}/mediavision/datasets/projects/${encodeURIComponent(projectId)}/effective-permissions`,
    },
  },

  // Feature Flags
  FEATURE_FLAGS: `${API_PREFIX}/feature-flags`,
};

// File/Upload URLs (for direct access, not through API)
export const UPLOAD_URLS = {
  PATIENTS: (filename) => `${API_BASE_URL}/uploads/patients/${filename}`,
  OUTPUT: (filename) => `${API_BASE_URL}/output/${filename}`,
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  API_BASE_URL,
  API_PREFIX,
  API_URL,
  API_ENDPOINTS,
  UPLOAD_URLS,
};
